import { useState, useEffect } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { useTranslation } from "react-i18next";
import AdminPageTitle from "@/components/admin/AdminPageTitle";
import { getMaintenanceSettings, setMaintenanceSettings } from "@/services/systemSettings";

export default function SystemSettingsPage() {
  const { t } = useTranslation();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [savedMaintenanceMessage, setSavedMaintenanceMessage] = useState("");
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);
  const [maintenanceSaving, setMaintenanceSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getMaintenanceSettings()
      .then((settings) => {
        if (!cancelled) {
          const message = settings.message ?? "";
          setMaintenanceMode(settings.enabled);
          setMaintenanceMessage(message);
          setSavedMaintenanceMessage(message);
        }
      })
      .catch(() => {
        // 读取失败时保持默认关闭。
        if (!cancelled) setMaintenanceMode(false);
      })
      .finally(() => {
        if (!cancelled) setMaintenanceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const saveMaintenance = async (nextEnabled: boolean, nextMessage: string) => {
    if (maintenanceSaving) return;

    const normalizedMessage = nextMessage.trim();
    if (nextEnabled && !normalizedMessage) {
      setSaveError(t("maintenance.messageRequired"));
      return;
    }

    setMaintenanceSaving(true);
    setSaveError("");
    setSaved(false);
    try {
      const settings = await setMaintenanceSettings({
        enabled: nextEnabled,
        message: normalizedMessage || null,
      });
      const confirmedMessage = settings.message ?? "";
      setMaintenanceMode(settings.enabled);
      setMaintenanceMessage(confirmedMessage);
      setSavedMaintenanceMessage(confirmedMessage);
      setSaved(true);
    } catch {
      setSaveError(t("maintenance.saveFailed"));
    } finally {
      setMaintenanceSaving(false);
    }
  };

  const toggleMaintenance = async (next: boolean) => {
    await saveMaintenance(next, maintenanceMessage);
  };

  return (
    <AdminLayout>
      <AdminPageTitle
        className="mb-6"
        title={<>System Settings</>}
        tooltip={t("maintenance.description")}
      />
      <div className="max-w-2xl">
        <div className="border border-gray-100 rounded-xl p-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-700">
                {t("maintenance.description")}
              </div>
            </div>
            <div className="shrink-0">
              {maintenanceLoading ? (
                <i className="ri-loader-4-line animate-spin text-sm text-gray-400"></i>
              ) : (
                <Toggle
                  value={maintenanceMode}
                  onChange={toggleMaintenance}
                  disabled={maintenanceSaving}
                />
              )}
            </div>
          </div>

          <div className="mt-5 border-t border-gray-100 pt-5">
            <label
              htmlFor="maintenance-message"
              className="block text-sm font-semibold text-gray-700"
            >
              {t("maintenance.message")}
            </label>
            <p className="mt-1 text-xs text-gray-500">{t("maintenance.messageHint")}</p>
            <div className="mt-3">
              <textarea
                id="maintenance-message"
                value={maintenanceMessage}
                onChange={(event) => {
                  setMaintenanceMessage(event.target.value);
                  setSaveError("");
                  setSaved(false);
                }}
                rows={4}
                maxLength={500}
                placeholder={t("maintenance.pageMessage")}
                disabled={maintenanceLoading || maintenanceSaving}
                className="w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm leading-6 text-gray-700 outline-none transition-colors placeholder:text-gray-400 focus:border-[#830051] focus:ring-2 focus:ring-[#830051]/10 disabled:cursor-not-allowed disabled:bg-gray-50"
              />
              <div className="mt-1 text-right text-[11px] text-gray-400">
                {maintenanceMessage.length}/500
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={() => void saveMaintenance(maintenanceMode, maintenanceMessage)}
                disabled={
                  maintenanceLoading ||
                  maintenanceSaving ||
                  maintenanceMessage === savedMaintenanceMessage
                }
                className="h-9 rounded-lg bg-[#830051] px-4 text-xs font-medium text-white transition-colors hover:bg-[#6f0045] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {maintenanceSaving ? t("syncConfig.saving") : t("maintenance.save")}
              </button>
              {saved && (
                <span className="text-xs font-medium text-emerald-600">
                  <i className="ri-check-line mr-1"></i>
                  {t("maintenance.saved")}
                </span>
              )}
            </div>
            {saveError && <p className="mt-2 text-xs text-red-600">{saveError}</p>}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function Toggle({
  value,
  onChange,
  disabled,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={() => onChange(!value)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#830051]/40 ${value ? "bg-[#830051]" : "bg-gray-300"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      role="switch"
      aria-checked={value}
    >
      <span
        className={`pointer-events-none absolute left-0.5 top-0.5 h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out ${value ? "translate-x-5" : "translate-x-0"}`}
      />
    </button>
  );
}
